import { inject } from '@angular/core'
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanMatchFn,
  Route,
  Router,
  RouterStateSnapshot,
  Routes,
  UrlSegment
} from '@angular/router'
import { BackendService } from './backend.service'
import { TokenData, UserRole } from './model/user-model'
import { UserService } from './services/user/user.service'
import { filter, firstValueFrom } from 'rxjs'
import { DirectusRoles } from 'src/types'

const canLoadPage: CanMatchFn = (route: Route, segments: UrlSegment[]) => {
  return true
}

const AuthGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const backendSvc = inject(BackendService)
  const tokenData = await backendSvc.extractTokenDataFromLocalStorage()

  if (tokenData?.[TokenData.AccessToken]) {
    backendSvc.refreshToken()

    return true
  } else {
    backendSvc.autoLogin()

    return false
  }
}

export const RoleGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const userSvc = inject(UserService)
  const router = inject(Router)
  const user = await firstValueFrom(userSvc.user$.pipe(filter((user) => user)))
  const userRole = (user?.role as Partial<DirectusRoles>)?.name ?? ''

  if (!route.data.role.includes(userRole)) {
    const url = userSvc.navigationUrl(userRole)
    router.navigateByUrl(url)
    return false
  }

  return true
}

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./login/login.page').then((m) => m.LoginPage)
  },
  {
    path: 'teacher',
    loadChildren: () => import('./teacher/teacher.routes').then((m) => m.routes),
    canActivate: [AuthGuard, RoleGuard],
    canMatch: [canLoadPage],
    data: { role: [UserRole.Teacher] }
  },
  {
    path: 'student',
    loadChildren: () => import('./student/student.routes').then((m) => m.routes),
    canActivate: [AuthGuard, RoleGuard],
    canMatch: [canLoadPage],
    data: { role: [UserRole.Student] }
  },
  {
    path: 'sign-up',
    loadComponent: () => import('./sign-up/sign-up.page').then((m) => m.SignUpPage),
    canActivate: [AuthGuard],
    canMatch: [canLoadPage]
  },
  {
    path: 'dictionary',
    loadChildren: () => import('./dictionary/dictionary.routes').then((m) => m.routes),
    canActivate: [AuthGuard],
    canMatch: [canLoadPage]
  },
  {
    path: 'achievements',
    loadChildren: () => import('./achievements/achievements.routes').then((m) => m.routes),
    canActivate: [AuthGuard, RoleGuard],
    canMatch: [canLoadPage],
    data: { role: [UserRole.Student] }
  },
  {
    path: 'profile',
    loadComponent: () => import('./profile/profile.page').then((m) => m.ProfilePage),
    canActivate: [AuthGuard],
    canMatch: [canLoadPage]
  },
  {
    path: 'my-students',
    loadComponent: () => import('./my-students/my-students.component').then((m) => m.MyStudentsComponent),
    canActivate: [AuthGuard, RoleGuard],
    canMatch: [canLoadPage],
    data: { role: [UserRole.Teacher] }
  },
  {
    path: 'register',
    loadComponent: () => import('./register-form/register-form.page').then((m) => m.RegisterFormPage)
  },
  {
    path: 'confirm-registration',
    loadComponent: () => import('./user-verify/user-verify.page').then((m) => m.UserVerifyPage)
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./forgot-password/forgot-password.page').then((m) => m.ForgotPasswordPage)
  },
  {
    path: 'change-password',
    loadComponent: () => import('./change-password/change-password.page').then((m) => m.ChangePasswordPage)
  },
  {
    path: 'help-page',
    loadComponent: () => import('./help-page/help-page.page').then((m) => m.HelpPage)
  },
  {
    path: 'content-management',
    loadChildren: () => import('./content-management/content-management.routes').then((m) => m.routes),
    canActivate: [AuthGuard, RoleGuard],
    canMatch: [canLoadPage],
    data: { role: [UserRole.ContentManager, UserRole.UserAdmin] }
  }
]
